import { useEffect, useState, type FormEvent } from 'react';

import { Modal } from '../ui';
import { api } from '../../lib/api';
import type { GroupView, RuleView, SystemView } from './types';
import { errorText } from './types';

type Action = 'ALLOW' | 'DENY';
type Proto = 'tcp' | 'udp' | 'any';
type Scope = 'SUBNET' | 'SERVER' | 'GROUP';

const DAY = 86_400_000;

function toDateInput(iso: string | null): string {
  return iso ? iso.slice(0, 10) : '';
}

function endOfDay(date: string): string {
  return new Date(`${date}T23:59:59Z`).toISOString();
}

function parseSources(text: string): string[] {
  return text
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/** A sentence that says what the rule will do, so nobody has to decode the fields. */
function summarise(p: {
  action: Action;
  protocol: Proto;
  portFrom: string;
  portTo: string;
  sources: string[];
  scope: Scope;
  targetName: string;
  expiresAt: string;
}): string {
  const verb = p.action === 'ALLOW' ? 'Allow' : 'Block';
  const port =
    p.protocol === 'any'
      ? 'all traffic'
      : `${p.protocol.toUpperCase()} ${p.portFrom || '?'}${p.portTo && p.portTo !== p.portFrom ? `–${p.portTo}` : ''}`;
  const from = p.sources.length ? `from ${p.sources.join(', ')}` : 'from anywhere';
  const to =
    p.scope === 'SUBNET' ? 'to every system on the managed network' : `to ${p.targetName || '…'}`;
  const until = p.action === 'ALLOW' && p.expiresAt ? `, until ${p.expiresAt}` : '';
  return `${verb} ${port} ${from} ${to}${until}.`;
}

export function RuleDialog({
  open,
  onClose,
  onSaved,
  editing,
  systems,
  groups,
}: {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  editing: RuleView | null;
  systems: SystemView[];
  groups: GroupView[];
}) {
  const [action, setAction] = useState<Action>('ALLOW');
  const [protocol, setProtocol] = useState<Proto>('tcp');
  const [portFrom, setPortFrom] = useState('');
  const [portTo, setPortTo] = useState('');
  const [sourcesText, setSourcesText] = useState('');
  const [scope, setScope] = useState<Scope>('SUBNET');
  const [targetSystem, setTargetSystem] = useState('');
  const [targetGroup, setTargetGroup] = useState('');
  const [description, setDescription] = useState('');
  const [justification, setJustification] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [reviewBy, setReviewBy] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    if (editing) {
      setAction(editing.action);
      setProtocol(editing.protocol);
      setPortFrom(editing.portStart ? String(editing.portStart) : '');
      setPortTo(editing.portEnd && editing.portEnd !== editing.portStart ? String(editing.portEnd) : '');
      setSourcesText(editing.sources.join(', '));
      setScope(editing.scope);
      setTargetSystem(editing.scope === 'SERVER' ? editing.targetId ?? '' : '');
      setTargetGroup(editing.scope === 'GROUP' ? editing.targetId ?? '' : '');
      setDescription(editing.description ?? '');
      setJustification(editing.businessJustification ?? '');
      setExpiresAt(toDateInput(editing.expiresAt));
      setReviewBy(toDateInput(editing.reviewBy));
    } else {
      setAction('ALLOW');
      setProtocol('tcp');
      setPortFrom('');
      setPortTo('');
      setSourcesText('');
      setScope('SUBNET');
      setTargetSystem(systems[0]?.id ?? '');
      setTargetGroup(groups[0]?.id ?? '');
      setDescription('');
      setJustification('');
      const in30 = new Date(Date.now() + 30 * DAY).toISOString().slice(0, 10);
      const in14 = new Date(Date.now() + 14 * DAY).toISOString().slice(0, 10);
      setExpiresAt(in30);
      setReviewBy(in14);
    }
  }, [open, editing, systems, groups]);

  const targetName =
    scope === 'SERVER'
      ? systems.find((s) => s.id === targetSystem)?.name ?? ''
      : scope === 'GROUP'
        ? groups.find((g) => g.id === targetGroup)?.name ?? ''
        : 'every system';

  const sources = parseSources(sourcesText);
  const isAllow = action === 'ALLOW';
  const justificationOk = !isAllow || justification.trim().length >= 20;
  const targetOk = scope === 'SUBNET' || (scope === 'SERVER' ? !!targetSystem : !!targetGroup);
  const portOk = protocol === 'any' || /^\d+$/.test(portFrom);
  const ready = targetOk && portOk && justificationOk;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const payload = {
      action,
      protocol,
      surface: 'DOCKER',
      scope,
      targetServerId: scope === 'SERVER' ? targetSystem : null,
      groupId: scope === 'GROUP' ? targetGroup : null,
      portStart: protocol === 'any' ? null : Number(portFrom),
      portEnd: protocol === 'any' || !portTo ? null : Number(portTo),
      sources,
      description,
      enabled: editing ? editing.enabled : true,
      businessJustification: isAllow ? justification.trim() : null,
      expiresAt: isAllow && expiresAt ? endOfDay(expiresAt) : null,
      reviewBy: isAllow && reviewBy ? endOfDay(reviewBy) : null,
    };
    try {
      if (editing) {
        await api(`/connectiq/firewall/rules/${editing.id}`, { method: 'PATCH', body: JSON.stringify(payload) });
      } else {
        await api('/connectiq/firewall/rules', { method: 'POST', body: JSON.stringify(payload) });
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={editing ? 'Edit rule' : 'Add a rule'} wide>
      <form className="ciq-form" onSubmit={submit}>
        <fieldset className="ciq-group">
          <legend>What</legend>
          <div className="ciq-seg" role="radiogroup" aria-label="Action">
            {(['ALLOW', 'DENY'] as const).map((a) => (
              <button key={a} type="button" role="radio" aria-checked={action === a} className={action === a ? 'on' : ''} onClick={() => setAction(a)}>
                {a === 'ALLOW' ? 'Allow' : 'Block'}
              </button>
            ))}
          </div>
          <div className="ciq-row">
            <div className="ciq-seg" role="radiogroup" aria-label="Protocol">
              {(['tcp', 'udp', 'any'] as const).map((p) => (
                <button key={p} type="button" role="radio" aria-checked={protocol === p} className={protocol === p ? 'on' : ''} onClick={() => setProtocol(p)}>
                  {p === 'any' ? 'Any' : p.toUpperCase()}
                </button>
              ))}
            </div>
            {protocol !== 'any' && (
              <>
                <label className="ciq-field">
                  <span>Port</span>
                  <input inputMode="numeric" value={portFrom} onChange={(e) => setPortFrom(e.target.value)} placeholder="443" />
                </label>
                <label className="ciq-field">
                  <span>to (optional)</span>
                  <input inputMode="numeric" value={portTo} onChange={(e) => setPortTo(e.target.value)} placeholder="8443" />
                </label>
              </>
            )}
          </div>
        </fieldset>

        <fieldset className="ciq-group">
          <legend>From</legend>
          <label className="ciq-field">
            <span>Source addresses (CIDR, separated by commas or spaces). Leave empty for anywhere.</span>
            <input value={sourcesText} onChange={(e) => setSourcesText(e.target.value)} placeholder="203.0.113.0/24, 198.51.100.7/32" />
          </label>
          {sources.length > 0 && (
            <div className="ciq-chips">
              {sources.map((s) => (
                <span key={s} className="ciq-chip mono">{s}</span>
              ))}
            </div>
          )}
        </fieldset>

        <fieldset className="ciq-group">
          <legend>To</legend>
          <div className="ciq-seg" role="radiogroup" aria-label="Applies to">
            {(
              [
                ['SUBNET', 'Every system'],
                ['SERVER', 'One system'],
                ['GROUP', 'A group'],
              ] as const
            ).map(([value, label]) => (
              <button key={value} type="button" role="radio" aria-checked={scope === value} className={scope === value ? 'on' : ''} onClick={() => setScope(value)}>
                {label}
              </button>
            ))}
          </div>
          {scope === 'SERVER' && (
            <label className="ciq-field">
              <span>System</span>
              <select value={targetSystem} onChange={(e) => setTargetSystem(e.target.value)}>
                {systems.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name} · {s.address ?? 'no address'}
                  </option>
                ))}
              </select>
            </label>
          )}
          {scope === 'GROUP' && (
            <label className="ciq-field">
              <span>Group</span>
              <select value={targetGroup} onChange={(e) => setTargetGroup(e.target.value)}>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name} · {g.memberIds.length} member{g.memberIds.length === 1 ? '' : 's'}
                  </option>
                ))}
              </select>
            </label>
          )}
        </fieldset>

        {isAllow && (
          <fieldset className="ciq-group">
            <legend>Why and for how long</legend>
            <label className="ciq-field">
              <span>Business justification ({justification.trim().length}/20 minimum)</span>
              <textarea rows={2} value={justification} onChange={(e) => setJustification(e.target.value)} />
            </label>
            <div className="ciq-row">
              <label className="ciq-field">
                <span>Expires on (max 365 days)</span>
                <input type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
              </label>
              <label className="ciq-field">
                <span>Review on</span>
                <input type="date" value={reviewBy} onChange={(e) => setReviewBy(e.target.value)} />
              </label>
            </div>
          </fieldset>
        )}

        <label className="ciq-field">
          <span>Note (optional)</span>
          <input value={description} onChange={(e) => setDescription(e.target.value)} maxLength={200} />
        </label>

        <p className="ciq-summary" aria-live="polite">
          {summarise({ action, protocol, portFrom, portTo, sources, scope, targetName, expiresAt })}
        </p>

        <div className="ciq-actions">
          <button type="button" className="ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" disabled={busy || !ready}>
            {busy ? 'Saving…' : editing ? 'Save changes' : 'Add rule'}
          </button>
        </div>
        {error && <p className="form-error" role="alert">{error}</p>}
      </form>
    </Modal>
  );
}
